import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Plane } from "lucide-react";
import { NAV_LINKS, WHATSAPP_URL } from "../../config/site";
import { useEnquiry } from "../../lib/site-ui";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const { openEnquiry } = useEnquiry();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  // On the homepage use in-page hash (smooth scroll); elsewhere link back to home section.
  const to = (hash) => (isHome ? hash : `/${hash}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-slate-950/70 backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href={isHome ? "#home" : "/"} className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <span className="grid place-items-center w-9 h-9 border border-white/20 rounded-sm">
            <Plane className="w-4 h-4 -rotate-45 text-white transition-transform group-hover:rotate-0 duration-500" />
          </span>
          <span className="font-display text-lg tracking-tight text-white leading-none">
            AeroVaid <span className="text-slate-500 font-normal hidden sm:inline">Aviation Academy</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={to(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-slate-300 hover:text-white transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-white after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="nav-whatsapp" className="text-sm text-slate-300 hover:text-white transition-colors">
            WhatsApp
          </a>
          <button
            data-testid="nav-demo-btn"
            onClick={() => openEnquiry()}
            className="bg-white text-slate-950 text-sm font-semibold px-5 py-2.5 rounded-sm hover:bg-slate-200 transition-all hover:scale-[1.03]"
          >
            Book Free Demo
          </button>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpenMenu((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {openMenu ? <X /> : <Menu />}
        </button>
      </nav>

      {openMenu && (
        <div className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-b border-white/10 px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={to(l.href)} onClick={() => setOpenMenu(false)} className="text-slate-200 text-lg py-1">
              {l.label}
            </a>
          ))}
          <button
            data-testid="mobile-demo-btn"
            onClick={() => { setOpenMenu(false); openEnquiry(); }}
            className="mt-2 bg-white text-slate-950 font-semibold px-5 py-3 rounded-sm"
          >
            Book Free Demo
          </button>
        </div>
      )}
    </header>
  );
};
